import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { antGrey, rwColors } from '../styles/globalStyles';
import { AppStoreType, AutoStagedRolloutCadence, PhasedRelease } from '../../types';
import { Nullable } from '../../typescriptHelpers';
import {
    phasedReleasePausedDisplayTextForAppStoreType,
    phasedReleaseDisplayTextForAppStoreType,
} from '../../helpers/displayHelpers';
import { formatResumedAtString } from '../monitoring/PhasedReleaseCard';

interface PhasedReleaseDayTextProps {
    appId: string;
    appStoreType: AppStoreType;
    phasedReleaseDay: number;
    phasedRelease: PhasedRelease;
    isPhasedReleasePaused: boolean;
    // Only true for a rollout that completed by being accelerated — the day is hidden otherwise.
    isRolloutComplete: boolean;
    androidStagedRolloutNotStarted: boolean;
    textStyle?: React.CSSProperties;
    maxWidth?: number;
    daysToPercent: Nullable<number[]>;
    currentPercent?: number;
    isAutomatedPhasedRollout: boolean;
}

const PhasedReleaseDayText: React.FC<PhasedReleaseDayTextProps> = (props: PhasedReleaseDayTextProps) => {
    const dayCount = props.isAutomatedPhasedRollout ? (props.daysToPercent?.length ?? 0) : 0;
    const isHourly = props.phasedRelease.autoStagedRolloutCadence === AutoStagedRolloutCadence.Hourly;
    const incrementUnit = isHourly ? 'hour' : 'day';

    const phasedDisplayText = phasedReleasePausedDisplayTextForAppStoreType(props.appStoreType);
    const phasedReleaseText = phasedReleaseDisplayTextForAppStoreType(props.appStoreType);

    const baseTextStyle: React.CSSProperties = {
        fontSize: 14,
        color: antGrey.grey7,
        textAlign: 'right',
    };
    const textStyles: React.CSSProperties =
        props.textStyle ?
            {
                ...baseTextStyle,
                ...props.textStyle,
            }
        :   { ...baseTextStyle };

    // When the rollout is halted at 100%, the rollout is effectively over: the "Day N of an
    // M-day rollout" framing (and the blackout copy) is meaningless — the saved rollout day is
    // derived from the percentage→schedule-day mapping, so a release promoted straight to 100%
    // reports the schedule's final day regardless of elapsed time. Show no day text in this case.
    const isHaltedAtFullRollout =
        props.isPhasedReleasePaused && (props.phasedRelease?.phasedReleasePercent ?? 0) >= 100;
    if (isHaltedAtFullRollout) {
        return null;
    }

    // The blackout pause is performed by Runway (isRunwayPaused=true). A user manually halting
    // the rollout leaves isRunwayPaused=false, even if we happen to be inside a blackout window.
    // Don't let the blackout copy mask the real reason (manual halt) in that case.
    const isManuallyHalted = props.isPhasedReleasePaused && !props.phasedRelease?.isRunwayPaused;
    const shouldShowBlackoutText = !!props.phasedRelease?.isOnBlackoutPeriod && !isManuallyHalted;

    const phasedReleaseTextRender = (shouldShowBlackoutText: boolean, androidStagedRolloutNotStarted: boolean) => {
        if (androidStagedRolloutNotStarted) {
            const currentPhasedReleasePercent =
                typeof props.currentPercent === 'number' && !Number.isNaN(props.currentPercent) ?
                    props.currentPercent
                :   props.daysToPercent?.[props.phasedReleaseDay - 1];

            if (currentPhasedReleasePercent === undefined) {
                return (
                    <Text style={textStyles}>
                        Your automated rollout sequence has not started; the staged rollout percentage will not change
                        until you start the rollout sequence.
                    </Text>
                );
            }

            return (
                <Text style={textStyles}>
                    Your automated rollout sequence has not started; staged rollout percentage will remain at{' '}
                    {currentPhasedReleasePercent}% until you start the rollout sequence.
                </Text>
            );
        }

        if (shouldShowBlackoutText) {
            return (
                <Text style={textStyles}>
                    Your {dayCount > 0 ? `${dayCount}-${incrementUnit} ` : ''}
                    {phasedReleaseText} has been{' '}
                    <Text strong style={{ color: antGrey.grey7 }}>
                        Paused
                    </Text>{' '}
                    due to a scheduled blackout period.{' '}
                    {props.phasedRelease.blackoutEndsAt ?
                        `It will resume ${formatResumedAtString(props.phasedRelease.blackoutEndsAt, props.phasedRelease.autoStagedRolloutCustomHour)}`
                    :   ''}
                </Text>
            );
        }

        const dayLabelColor =
            props.isPhasedReleasePaused ? rwColors.statusRed
            : props.phasedRelease?.isSuperseded ? antGrey.grey7
            : props.isRolloutComplete ? rwColors.statusGreen
            : rwColors.primary;

        return (
            <Text style={textStyles}>
                {props.phasedRelease?.isSuperseded == true ?
                    'Superseded on '
                : props.isPhasedReleasePaused ?
                    `${phasedDisplayText} on `
                : props.isRolloutComplete ?
                    'Completed on '
                :   'Today is '}
                <Text strong style={{ color: dayLabelColor }}>
                    {isHourly ? 'Hour' : 'Day'} {props.phasedReleaseDay}
                </Text>
                {dayCount > 0 ? ` of a ${dayCount}-${incrementUnit} ${phasedReleaseText}` : ''}
            </Text>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', maxWidth: props.maxWidth }}>
            {phasedReleaseTextRender(shouldShowBlackoutText, props.androidStagedRolloutNotStarted)}
        </div>
    );
};

export default PhasedReleaseDayText;
